import type { FkspRequest } from '../types'
import type { FkspProcessedFilter, FkspStatusFilter, FkspTypeFilter, FkspFilledFilter, FkspEkonomSortField } from './fkspConstants'
import { comparePersonNames } from '../utils/personNameSort'

/** Čas podání žádosti pro řazení (created_at). */
export function fkspCreatedAtMs(req: FkspRequest): number {
  const t = Date.parse(req.created_at || '')
  return Number.isFinite(t) ? t : 0
}

function normalizeSearch(s: string): string {
  return s
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/\p{M}/gu, '')
}

/** Shoda podle příjmení / jména (celý řetězec, část před čárkou, osobní číslo). */
export function fkspMatchesNameFilter(req: FkspRequest, queryRaw: string): boolean {
  const q = normalizeSearch(queryRaw)
  if (!q) return true
  const name = (req.requester_name || '').trim()
  const hay = normalizeSearch(name)
  if (hay.includes(q)) return true
  const beforeComma = name.split(',')[0]?.trim() || ''
  if (beforeComma && normalizeSearch(beforeComma).includes(q)) return true
  const pn = (req.personal_number || '').trim().toLowerCase()
  if (pn && pn.includes(queryRaw.trim().toLowerCase())) return true
  return false
}

export type FkspListFilterParams = {
  searchQuery: string
  dateNewestFirst: boolean
  createdFrom?: string
  createdTo?: string
  typeFilter?: FkspTypeFilter
  processedFilter?: FkspProcessedFilter
  statusFilter?: FkspStatusFilter
  benefitSubtypeFilter?: string
  doplacenoFilter?: FkspFilledFilter
  datumFilter?: FkspFilledFilter
  sortField?: FkspEkonomSortField
}

function dateOnlyMs(value: string | null | undefined): number {
  if (!value) return Number.NaN
  const day = String(value).slice(0, 10)
  const t = Date.parse(`${day}T00:00:00`)
  return Number.isFinite(t) ? t : Number.NaN
}

function inCreatedDateRange(createdAt: string | undefined, from?: string, to?: string): boolean {
  if (!from && !to) return true
  return inDateRangeDay(createdAt, from, to)
}

function inDateRangeDay(value: string | null | undefined, from?: string, to?: string): boolean {
  if (!from && !to) return true
  const ms = dateOnlyMs(value)
  if (!Number.isFinite(ms)) return false
  if (from) {
    const f = dateOnlyMs(from)
    if (Number.isFinite(f) && ms < f) return false
  }
  if (to) {
    const t = dateOnlyMs(to)
    if (Number.isFinite(t) && ms > t) return false
  }
  return true
}

function fkspFinancistDatumMs(req: FkspRequest): number {
  const raw = (req.financist_datum || '').trim()
  if (!raw) return Number.NaN
  const t = Date.parse(raw)
  return Number.isFinite(t) ? t : Number.NaN
}

function matchesFilledFilter(value: unknown, filter: FkspFilledFilter): boolean {
  if (filter === 'all') return true
  const filled =
    value !== null &&
    value !== undefined &&
    (typeof value === 'number' ? Number.isFinite(value) : String(value).trim() !== '')
  return filter === 'filled' ? filled : !filled
}

export function filterAndSortFkspRequests(list: FkspRequest[], params: FkspListFilterParams): FkspRequest[] {
  const {
    searchQuery,
    dateNewestFirst,
    createdFrom = '',
    createdTo = '',
    typeFilter = 'all',
    processedFilter = 'all',
    statusFilter = 'all',
    benefitSubtypeFilter = '',
    doplacenoFilter = 'all',
    datumFilter = 'all',
    sortField = 'created_at',
  } = params

  const subtypeQ = normalizeSearch(benefitSubtypeFilter)

  const filtered = list.filter((r) => {
    if (!fkspMatchesNameFilter(r, searchQuery)) return false
    if (!inCreatedDateRange(r.created_at, createdFrom, createdTo)) return false
    if (typeFilter !== 'all' && r.type !== typeFilter) return false
    if (statusFilter !== 'all' && r.status !== statusFilter) return false
    if (processedFilter === 'processed' && !r.financist_processed_at) return false
    if (processedFilter === 'unprocessed' && r.financist_processed_at) return false
    if (!matchesFilledFilter(r.financist_doplaceno, doplacenoFilter)) return false
    if (!matchesFilledFilter(r.financist_datum, datumFilter)) return false
    if (subtypeQ) {
      const bt = normalizeSearch(r.benefit_type || '')
      if (!bt.includes(subtypeQ)) return false
    }
    return true
  })

  const dir = dateNewestFirst ? -1 : 1

  return [...filtered].sort((a, b) => {
    if (sortField === 'requester_name') {
      const cmp = comparePersonNames({ name: a.requester_name }, { name: b.requester_name })
      return dateNewestFirst ? -cmp : cmp
    }
    if (sortField === 'doplaceno') {
      const av = a.financist_doplaceno
      const bv = b.financist_doplaceno
      const aEmpty = av === null || av === undefined
      const bEmpty = bv === null || bv === undefined
      if (aEmpty && bEmpty) return 0
      if (aEmpty) return 1
      if (bEmpty) return -1
      return dir * ((av as number) - (bv as number))
    }
    if (sortField === 'datum') {
      const ad = fkspFinancistDatumMs(a)
      const bd = fkspFinancistDatumMs(b)
      const aEmpty = !Number.isFinite(ad)
      const bEmpty = !Number.isFinite(bd)
      if (aEmpty && bEmpty) return 0
      if (aEmpty) return 1
      if (bEmpty) return -1
      return dir * (ad - bd)
    }
    const da = fkspCreatedAtMs(a)
    const db = fkspCreatedAtMs(b)
    return dateNewestFirst ? db - da : da - db
  })
}
